import default_avatar from '@/assets/images/default-avatar.jpg';
import { Button } from '@/components/Button';
import { Image } from '@/components/Image';
import { Card as CardType } from '@/types/card';
import getDefaultCover from '@/utils/getDefaultCover';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { IoEyeOutline } from 'react-icons/io5';
import { Badge } from './Badge';

interface CardProps {
  card: CardType;
  onShare?: () => void;
  onModify?: () => void;
}

export const CardOverview = ({ card, onShare, onModify }: CardProps) => {
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  const [maxVisitsCount, setMaxVisitsCount] = useState<number | string>(0);
  const [visitsCount, setVisitsCount] = useState<number>(0);
  const [visitsDisplay, setVisitsDisplay] = useState<string>('');

  useEffect(() => {
    const currentVisitsCount = card.monthly_visits_count;
    setVisitsCount(currentVisitsCount);

    const maxVisits = card.max_visits_count;
    setMaxVisitsCount(maxVisits);

    setVisitsDisplay(
      `${currentVisitsCount} / ${String(maxVisits) === 'infinite' ? '∞' : maxVisits}`,
    );
  }, [card]);

  return (
    <div
      onClick={onModify}
      className={`flex size-full flex-col items-center overflow-hidden rounded-2xl bg-primary shadow-md ${onModify ? 'cursor-pointer' : ''}`}
    >
      {/* Header */}
      <div className="relative mb-12 w-full">
        {/* Cover */}
        <Image
          src={card.cover || getDefaultCover(card.color)}
          alt="cover"
          className="h-32 w-full object-cover"
        />
        {/* Active/inactive badge */}
        <div className="absolute right-4 top-4">
          <Badge
            label={card.is_active ? t('common:active') : t('common:inactive')}
            color={card.is_active ? 'green' : 'red'}
          />
        </div>
        {/* Avatar */}
        <Image
          src={card.avatar || default_avatar}
          alt="avatar"
          className="absolute bottom-0 left-1/2 size-24 -translate-x-1/2 translate-y-1/2 rounded-full border-4 border-primary"
        />
      </div>
      <div className="flex size-full flex-col items-center justify-between p-4">
        {/* Body */}
        <div className="mt-2 flex flex-col items-center justify-center text-center">
          <h2 className="text-xl font-bold">{card.name}</h2>
          <p className="text-sm text-gray-400">{card.full_name}</p>
          <Badge
            className="mt-4"
            label={visitsDisplay}
            color={
              typeof maxVisitsCount === 'number' &&
              visitsCount >= maxVisitsCount
                ? 'red'
                : 'blue'
            }
            icon={<IoEyeOutline className="size-4" />}
          />
        </div>
        {/* Actions */}
        {(onShare || onModify) && (
          <div
            className="mt-4 flex justify-center gap-4 p-4"
            onClick={stopPropagation}
          >
            {onModify && (
              <Button className="bg-secondary" onClick={onModify}>
                {t('common:modify')}
              </Button>
            )}
            {onShare && (
              <Button
                className="bg-linko-light disabled:text-white"
                onClick={onShare}
                disabled={!card.is_active}
              >
                {t('common:share')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
